
import { Vue, Component, Prop } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { clearSsoRecoveryAttempt, hasAttemptedSsoRecovery, markSsoRecoveryAttempted, resolveSsoGate } from '@/utils/sso';
import { fireGoogleTag } from '@/utils/google-tag-manager-helpers';
import 'vue-select/dist/vue-select.css';

const namespace: string = 'restaurant';

@Component<RestaurantIndex>({})
export default class RestaurantIndex extends Vue {
	@Prop({ type: String, required: true, default: '' }) private restaurantSlug!: string;
	@Action('fetchRestaurant', { namespace }) private fetchRestaurant!: (payload: FetchRestaurantPayload) => Promise<boolean>;
	@Action('logout', { namespace: 'auth' }) private logout!: () => Promise<void>;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('getRestaurantSso', { namespace }) private restaurantSso!: Sso | null;
	@Getter('hasCssLoaded', { namespace: 'restaurant' }) private cssLoaded!: boolean;
	@Getter('getUser', { namespace: 'auth' }) private user!: UserInfo;
	@Getter('getUserProvider', { namespace: 'auth' }) private userProvider!: string | null;
	@Getter('isAuthenticated', { namespace: 'auth' }) private isAuthenticated!: boolean;

	private dataFetched: boolean = false;

	private async created(): Promise<void> {
		await this.loadRestaurant();
	}

	/**
	 * Fetch the location if it is not already in the store, then enforce the SSO gate.
	 *
	 * @return {Promise<void>}
	 */
	private async loadRestaurant(): Promise<void> {
		// The store can already hold a different location, e.g. after an SSO login routed the user
		// to the slug their role is allowed to order from, so the slug has to be compared too
		const loadedSlug: string = this.restaurant?.slug ? this.restaurant.slug.toLowerCase() : '';

		if(!this.restaurant || !this.restaurant.name || loadedSlug !== this.restaurantSlug.toLowerCase()) {
			try {
				await this.fetchRestaurant({
					restaurantSlug: this.restaurantSlug,
					route: this.$route,
					router: this.$router
				});
			}
			catch (error) {
				if (error.errorMessage) {
					this.$toasted.show(error.errorMessage, { type: 'error', position: 'top-center' }).goAway(5000);
				}
				else {
					this.$router.push({ name: 'PageNotFound' }).catch(() => {});
				}

				this.dataFetched = true;
				return;
			}
		}

		if (this.restaurantSso && await this.applySsoGate()) {
			return;
		}

		this.dataFetched = true;
	}

	/**
	 * Enforce the SSO rules for the location that was just loaded.
	 * - Login means user isn't logged in yet and has to silent login
	 * - Redirect means user is logged in but does not have access to that specific location (normal vs executive)
	 * - Denied can happen for many reasons, but this means the user should not have access to this menu
	 *
	 * @return {Promise<boolean>}
	 */
	private async applySsoGate(): Promise<boolean> {
		const gate = resolveSsoGate(this.isAuthenticated, this.userProvider, this.restaurantSso!.type, hasAttemptedSsoRecovery());

		switch (gate.action) {
			case 'login':
				this.$router.replace({ path: '/login', query: this.$route.query }).catch(() => {});
				return true;

			// A session from a regular account, or from another provider, cannot be placed on an SSO location, so clear it and let the SSO
			// flow run rather than leaving the user stuck until they wipe their own cookies
			case 'logout':
				fireGoogleTag({ name: 'socialLoginOrRegisterError', specifier: this.restaurantSso!.type, detail: 'session_provider_mismatch' });
				markSsoRecoveryAttempted();
				try {
					await this.logout();
				}
				catch (error) {
					// logout redirects to /login itself when it fails
					return true;
				}
				this.$router.replace({ path: '/login', query: this.$route.query }).catch(() => {});
				return true;

			case 'denied':
				this.$router.replace({ name: 'AccessDenied' }).catch(() => {});
				return true;

			default:
				clearSsoRecoveryAttempt();
				return false;
		}
	}
}
